import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { SectionTitle, DataTable, LabelledList, EventPageHero } from "@/components/PageBlocks";
import { ArrowRight } from "lucide-react";
import { BAPTEME_CONTENT } from "@/content/bapteme";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { Rich } from "@/components/Rich";

const SITE = "https://lentracterestaurant.fr";

const BAPTEME_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE}/bapteme#service`,
  "name": "Repas de baptême et de communion à L'Entracte",
  "serviceType": "Repas de baptême et de communion",
  "description":
    "Déjeuner de baptême ou de communion dans un espace privatisé d'un hôtel particulier à Ville-d'Avray, menu fait maison sur-mesure.",
  "provider": { "@id": `${SITE}/#lieu` },
  "areaServed": [
    { "@type": "City", "name": "Ville-d'Avray" },
    { "@type": "City", "name": "Versailles" },
    { "@type": "City", "name": "Sèvres" },
    { "@type": "City", "name": "Chaville" },
    { "@type": "City", "name": "Saint-Cloud" },
  ],
};

export default function Bapteme() {
  const { lang, localePath, alternateUrls } = useI18n();
  const c = usePageContent("bapteme", lang === "en" ? "en" : "fr", BAPTEME_CONTENT[lang === "en" ? "en" : "fr"]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: [
      breadcrumb([
        { name: lang === "en" ? "Home" : "Accueil", url: `${SITE}/` },
        { name: lang === "en" ? "Events" : "Événements", url: `${SITE}/evenements` },
        {
          name: lang === "en" ? "Christening & communion" : "Baptême & communion",
          url: `${SITE}/bapteme`,
        },
      ]),
      BAPTEME_SERVICE_LD,
    ],
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const HERO = pageImageUrl(imgs, "bapteme", "hero");
  const TERRACE = pageImageUrl(imgs, "bapteme", "terrace");

  const crumbs = c.breadcrumb.map((b) => ({ label: b.label, to: b.to ? localePath(b.to) : undefined }));

  return (
    <div data-testid="bapteme-page">
      <EventPageHero
        overline={c.overline}
        h1={c.h1}
        intro={c.intro}
        ctaDevis={c.ctaDevis}
        ctaCall={c.ctaCall}
        image={HERO}
        alt={c.heroAlt}
        crumbs={crumbs}
        phoneSource="bapteme_hero"
      />

      {/* EN BREF */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid="bapteme-brief-table" />
          </div>
        </div>
      </section>

      {/* DÉJEUNER DE FAMILLE */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-7">
            <SectionTitle>{c.family.title}</SectionTitle>
            <Rich as="p" value={c.family.body} className="mt-8 text-[#3D4A5A] leading-relaxed max-w-2xl" />
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {TERRACE && (
                <img src={TERRACE} alt={c.family.alt} className="w-full h-full object-cover img-soft" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DÉROULÉ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.timeline.title}</SectionTitle>
          <ol className="mt-12 space-y-8" data-testid="bapteme-timeline">
            {c.timeline.steps.map((s, i) => (
              <li key={s.label} className="flex items-start gap-6">
                <span className="font-display text-3xl text-[#B8956A] leading-none w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-[#3D4A5A] leading-relaxed pt-1">
                  <Rich value={s.label} className="text-[#203240]" /> <Rich value={s.body} />
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* MENU FAMILLE */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.menu.title}</SectionTitle>
          <Rich as="p" value={c.menu.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <Link to={localePath("/menu")} className="btn-ghost mt-10 inline-flex">
            {c.menu.ctaMenu} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.why.title}</SectionTitle>
          <div className="mt-10">
            <LabelledList items={c.why.items} testid="bapteme-why" />
          </div>
        </div>
      </section>

      {/* ACCÈS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.access.title}</SectionTitle>
          <Rich as="p" value={c.access.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <p className="mt-6 text-[#697585] leading-relaxed">{c.access.note}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid="bapteme-faq" schemaId="ld-faq-bapteme" />
          </div>
        </div>
      </section>

      {/* DEVIS */}
      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#F6F0E7] p-7 md:p-10">
              <DevisForm defaultEventType={c.eventType} pageSource="/bapteme" />
            </div>
          </div>
        </div>
      </section>

      {/* LIENS INTERNES */}
      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#F6F0E7] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <p className="overline">{c.related.title}</p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            {c.related.links.map((l) => (
              <Link
                key={l.to}
                to={localePath(l.to)}
                className="inline-flex items-center gap-2 text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                {l.label} <ArrowRight size={14} className="text-[#B8956A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
